import { useMemo } from "react";
import PropTypes from "prop-types";
import { TransactionConfirmationModalContainer } from "./TransactionConfirmationModal.styles";
import { Button } from "components/Button";
import { Loader } from "components/Loader";
import { HelpCenterIcon } from "assets/icons";

const TransactionConfirmationModal = ({
  show,
  onHide,
  onConfirm,
  transactionData,
  isLoading = false,
  type = "deposit", // 'deposit' or 'withdraw'
}) => {
  const { amount, bankDetails } = transactionData || {};

  const formattedAmount = useMemo(() => {
    const numeric = Number(amount);
    const absoluteValue = Math.abs(numeric);
    return isNaN(absoluteValue)
      ? "0.00"
      : absoluteValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }, [amount]);

  // Dynamic content based on type
  const modalTitle =
    type === "deposit" ? "Deposit funds" : "Withdraw from Surmount";
  const infoText =
    type === "deposit"
      ? `Are you sure to deposit $${formattedAmount} from Surmount to this bank?`
      : `Are you sure you want to withdraw $${formattedAmount} from Surmount to this bank?`;
  const amountLabel = type === "deposit" ? "Deposit amount" : "Withdraw amount";
  const confirmButtonText = type === "deposit" ? "Deposit" : "Withdraw";
  return (
    <TransactionConfirmationModalContainer
      show={show}
      onHide={onHide}
      type={type}
    >
      <div className="modal-header">
        <h2 className="modal-title">{modalTitle}</h2>
      </div>

      <div className="modal-body-content">
        {/* Info Banner - show only for withdraw */}
        {type === "withdraw" && (
          <div className="info-banner">
            <div className="info-icon">
              <HelpCenterIcon />
            </div>
            <p className="info-text">{infoText}</p>
          </div>
        )}

        {/* Bank Details Section */}
        <div className="bank-details-section">
          <h4 className="section-title">Bank details</h4>

          <div className="bank-detail-row">
            <div className="detail-label">Name</div>
            <div className="detail-value">{bankDetails?.name || "--"}</div>
          </div>

          <div className="bank-detail-row">
            <div className="detail-label">Institution name</div>
            <div className="detail-value">
              {bankDetails?.institutionName || "--"}
            </div>
          </div>

          <div className="bank-detail-row">
            <div className="detail-label">Account type</div>
            <div className="detail-value">
              {bankDetails?.accountType || "--"}
            </div>
          </div>

          <div className="bank-detail-row">
            <div className="detail-label">Account number</div>
            <div className="detail-value">
              {bankDetails?.accountNumber || "--"}
            </div>
          </div>
        </div>

        {/* Amount Section */}
        <div className="amount-section">
          <div className="amount-label">{amountLabel}</div>
          <div className="amount-value">${formattedAmount} USD</div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <Button
            variant="secondary"
            size="large"
            width="49%"
            onClick={onHide}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            size="large"
            width="49%"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="button-content">
                <span className="button-loader">
                  <Loader />
                </span>
                <span>Processing...</span>
              </span>
            ) : (
              confirmButtonText
            )}
          </Button>
        </div>
      </div>
    </TransactionConfirmationModalContainer>
  );
};

TransactionConfirmationModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  transactionData: PropTypes.shape({
    amount: PropTypes.string,
    bankDetails: PropTypes.shape({
      name: PropTypes.string,
      institutionName: PropTypes.string,
      accountType: PropTypes.string,
      accountNumber: PropTypes.string,
    }),
    availableBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  isLoading: PropTypes.bool,
  type: PropTypes.oneOf(["deposit", "withdraw"]).isRequired,
};

export { TransactionConfirmationModal };
